import React, { useMemo } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { enqueueSnackbar } from 'notistack';
import { observer } from 'mobx-react';
import { InfoBox, Surface, TitleWithDescription } from '@infoods/lifebase-ui';

import classes from './MealTimeStep.module.scss';
import { ButtonsRow } from '../../components/ButtonsRow/ButtonsRow';
import {
  MealTimesTable,
  MealSchedule,
  mealScheduleService,
} from '../../../../../nutrition';
import { LbCheckbox } from '../../../../../shared/components/form-components';
import { WORKOUT_MEAL_NAME } from '../../../../../shared/constants/mealNames';
import { goTo } from '../../../../../shared/utils/angularUtils';
import { customerService } from '../../../../services/customerService';
import { getMealsListForMealsPerDay } from '../../../../../shared/utils/mealUtils';
import { customerSetupStore } from '../../../../stores/customerSetupStore';
import { pathQueryParams } from '../../../../../shared/constants/pathQueryParams';
import { CUSTOMER_PROFILE_TAB_NAMES } from '../../../../../shared/constants/tabNames/CUSTOMER_PROFILE_TAB_NAMES';

type MealTimeStepProps = {
  customerId: number;
};

const MealTimeStep = observer(({ customerId }: MealTimeStepProps) => {
  const formMethods = useForm<MealSchedule>({
    defaultValues: async () => mealScheduleService.getMealSchedule(customerId),
    shouldUnregister: false, // it crucial to send all data to the server, not exist data will be replaced by null
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting },
  } = formMethods;

  const { mealsPerDay } = customerSetupStore;

  const onSubmit = async (data: MealSchedule) => {
    await mealScheduleService.updateMealSchedule(customerId, {
      ...data,
      setImmediately: true,
    });
    await customerService.sendSetPasswordLink(customerId);

    enqueueSnackbar(
      'The customer is set up! The email with a magic link to set a password is sent to a customer',
      { variant: 'success' },
    );

    goTo(`/customer-profile/${customerId}`, {
      params: {
        [pathQueryParams.initialTabName]: CUSTOMER_PROFILE_TAB_NAMES.CUSTOMER_PROFILE,
      },
      replaceHistory: true,
    });
  };

  const WorkoutTitle = (
    <TitleWithDescription
      title="Specify workout days"
      description="Turn on days when the user has a workout."
    />
  );

  // delete this code when add selecting meal plans for days
  const mealsList = useMemo(() => getMealsListForMealsPerDay(mealsPerDay), [mealsPerDay]);

  return (
    <FormProvider {...formMethods}>
      <Surface className={classes.mealTable}>
        <MealTimesTable
          mealNames={mealsList}
          mealFormKey="mealTime"
          mealTitle="Meals"
          renderDayTitle={(day) => <p>{day}</p>}
        />
      </Surface>
      <InfoBox title={WorkoutTitle} customContentPadding className={classes.workoutTable}>
        <MealTimesTable
          mealNames={Object.values(WORKOUT_MEAL_NAME)}
          mealFormKey="workoutMealTime"
          mealTitle="Pre/Post Meals"
          renderDayTitle={(day) => (
            <LbCheckbox
              label={day}
              color="primary"
              control={control}
              name={`${day}.workoutMealTime.enabled`}
            />
          )}
          getIsDisabledTime={({ day }) => {
            return !watch(`${day}.workoutMealTime.enabled`);
          }}
        />
      </InfoBox>
      <ButtonsRow
        onNext={handleSubmit(onSubmit)}
        yesDisabled={isSubmitting}
        nextText="Complete user setup and Send Magic Link"
      />
    </FormProvider>
  );
});

export { MealTimeStep };
